/** ProjectPickerPage — 워크스페이스·프로젝트 목록 셸(P4-c). 선택·목록·생성/삭제/이동·오버레이는 features/project-picker/picker/ 의 모듈이고 여기서는 조립만 한다 */
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, LinearProgress } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { rootNavItems } from '@/app/chrome/rootNav';
import AppLayout from '@/app/chrome/AppLayout';
import HeaderUtilities from '@/shared/ui/HeaderUtilities';
import ToolbarSearchField from '@/shared/ui/ToolbarSearchField';
import { ProjectGrid, WorkspaceSection } from '@/features/project-picker';
import { useUiChromeStore } from '@/app/stores/uiChromeStore';
import { useLogout } from '@/features/auth/useLogout';
import { useWorkspaceSelection } from '@/features/project-picker/picker/useWorkspaceSelection';
import { useProjectListing } from '@/features/project-picker/picker/useProjectListing';
import { QUICKRUN_PROJECT_ID } from '@/app/quickRunProject';
import { useProjectMutations, type Notice } from '@/features/project-picker/picker/useProjectMutations';
import { PickerOverlays } from '@/features/project-picker/picker/PickerOverlays';
import ImportProjectFromJson from '@/features/project-picker/ImportProjectFromJson';

const ProjectPickerPage: React.FC = () => {
  const navigate = useNavigate();
  const { sidebarExpanded, toggleSidebar } = useUiChromeStore();
  const handleLogout = useLogout();
  const [notice, setNotice] = useState<Notice & { open: boolean }>({ open: false, message: '', severity: 'success' });
  const notify = useCallback((n: Notice) => setNotice({ ...n, open: true }), []);
  const ws = useWorkspaceSelection();
  const list = useProjectListing(ws.wsSelection);
  const m = useProjectMutations(ws.wsSelection, notify);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSelectProject = useCallback((projectId: string) => {
    if (projectId === QUICKRUN_PROJECT_ID) { if (list.quickRunVisible) navigate('/quickrun'); return; }
    navigate(`/projects/${projectId}`);
  }, [navigate, list.quickRunVisible]);

  return (
    <AppLayout
      sidebarExpanded={sidebarExpanded} onSidebarToggle={toggleSidebar} sidebarItems={rootNavItems('workspaces', navigate)}
      onLogout={handleLogout} onAccountSettings={() => navigate('/settings')}
      sidebarSection={<WorkspaceSection selection={ws.wsSelection} onSelect={ws.selectWorkspace} counts={list.workspaceCounts} />}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 3 }}>
        {/* 헤더 — 좌측 타이틀(선택 워크스페이스) · 중앙 검색(Simulations 와 공용) · 우측 버튼+유틸리티(줄바꿈 허용) */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" fontWeight={600}>{ws.headerTitle}</Typography>
          {/* 전환 중과 선택 보류(기본 행 id 는 목록을 받은 뒤에 안다) — 둘 다 진행 표시, 빈 목록을 거짓으로 그리지 않는다. 조회 실패는 진행이 아니다(그리드가 오류 카드) */}
          {(ws.switching || (ws.pending && !ws.loadFailed)) && <LinearProgress aria-label="워크스페이스 전환 중" sx={{ width: 120, height: 3, borderRadius: 2 }} />}
          <ToolbarSearchField value={searchQuery} onChange={setSearchQuery} placeholder="프로젝트 검색" />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => m.setNewProjectDialogOpen(true)} disabled={ws.pending} sx={{ textTransform: 'none' }}>NEW PROJECT</Button>
            <ImportProjectFromJson onCreate={m.handleCreateProjectFromFile} onCreated={(id) => navigate(`/projects/${id}`)} existingTitles={list.takenNames} workspaceLabel={ws.headerTitle} disabled={list.loading || ws.pending} />
            <HeaderUtilities />
          </Box>
        </Box>
        {/* 메인 — 우측 정보 패널은 랜딩으로 이관됨(2026-08-11). 그리드가 전폭을 쓴다 */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {/* 워크스페이스 목록을 못 받으면 보류는 진행이 아니라 오류다 — 카드로 보이고 재조회로 푼다 */}
          <ProjectGrid projects={list.displayProjects} loading={list.loading || (ws.pending && !ws.loadFailed)}
            error={list.error ?? (ws.loadFailed ? `워크스페이스 목록을 불러오지 못했습니다: ${ws.loadFailed}` : null)}
            onRetry={() => { void list.refetch(); void ws.reloadWorkspaces(); }} searchQuery={searchQuery}
            onSelectProject={handleSelectProject} onDeleteProject={m.handleDeleteClick} onMoveProject={m.handleMoveClick}
            onStartTutorial={() => navigate('/tutorial/projects-and-models')} />
        </Box>
      </Box>
      <PickerOverlays
        moveTarget={m.moveTarget} workspaces={ws.workspaces} onCloseMove={() => m.setMoveTarget(null)} onMove={m.handleMoveConfirm}
        newProjectDialogOpen={m.newProjectDialogOpen} onCloseNewProject={() => m.setNewProjectDialogOpen(false)} onCreated={(id) => navigate(`/projects/${id}`)} onCreate={m.handleCreateProject} loading={list.loading} error={list.error} existingTitles={list.takenNames}
        deleteDialogOpen={m.deleteDialogOpen} onCloseDelete={() => m.setDeleteDialogOpen(false)} onConfirmDelete={() => void m.handleDeleteConfirm()}
        forceConfirm={m.forceConfirm} onCancelForce={m.handleForceDeleteCancel} onConfirmForce={() => void m.handleForceDeleteConfirm()}
        notice={notice} onCloseNotice={() => setNotice((prev) => ({ ...prev, open: false }))}
      />
    </AppLayout>
  );
};

export default ProjectPickerPage;
